// components/TeamManagementView.js - Complete Enhanced Team Management with Dynamic Bill Rate Roles
import React, { useState, useEffect } from 'react';
import { Plus, X, Users, Trash2, Mail, Shield, UserCheck, UserX, Copy, Clock, AlertTriangle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { supabase } from '../lib/supabase';

const TeamManagementView = () => {
  const { user } = useAuth();
  const { currentTenant, userRole } = useTenant();
  
  const [teamMembers, setTeamMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(true);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('user');
  const [inviteBillRateRole, setInviteBillRateRole] = useState('');
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteMessage, setInviteMessage] = useState('');
  const [inviteResult, setInviteResult] = useState(null);
  const [sendEmail, setSendEmail] = useState(true);

  // NEW: State for dynamic bill rate roles
  const [billRateRoles, setBillRateRoles] = useState([]);
  const [loadingBillRates, setLoadingBillRates] = useState(true);

  // Helper function to check if user is admin
  const isAdmin = () => userRole === 'admin';

  // Load bill rate roles from database
  const loadBillRateRoles = async () => {
    try {
      setLoadingBillRates(true);
      
      const { data, error } = await supabase
        .from('bill_rates')
        .select('role, hourly_rate')
        .eq('tenant_id', currentTenant.id)
        .eq('is_default', true)
        .order('hourly_rate', { ascending: false });

      if (error) throw error;

      // Transform the data into the format expected by the dropdown
      const formattedRoles = data.map(rate => ({
        value: rate.role,
        label: formatRoleName(rate.role),
        rate: rate.hourly_rate
      }));

      setBillRateRoles(formattedRoles);
      
      // Set default selection to first role if none selected
      if (formattedRoles.length > 0 && !inviteBillRateRole) {
        setInviteBillRateRole(formattedRoles[0].value);
      }
    } catch (error) {
      console.error('Error loading bill rate roles:', error);
      // Fallback to basic roles if database fails
      const fallbackRoles = [
        { value: 'senior_developer', label: 'Senior Developer', rate: 150 },
        { value: 'developer', label: 'Developer', rate: 125 },
        { value: 'junior_developer', label: 'Junior Developer', rate: 100 },
        { value: 'designer', label: 'Designer', rate: 120 },
        { value: 'project_manager', label: 'Project Manager', rate: 140 }
      ];
      setBillRateRoles(fallbackRoles);
      if (!inviteBillRateRole) {
        setInviteBillRateRole(fallbackRoles[0].value);
      }
    } finally {
      setLoadingBillRates(false);
    }
  };

  // Format role name for display
  const formatRoleName = (role) => {
    return role.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Update billing after team changes
  const updateBillingAfterTeamChange = async () => {
    try {
      // Only update if tenant has active subscription
      if (currentTenant?.subscription_status === 'active') {
        const response = await supabase.functions.invoke('stripe-billing', {
          body: {
            action: 'update_seat_count',
            data: { tenant_id: currentTenant.id }
          }
        });

        if (response.data?.success) {
          console.log('Billing updated:', response.data);
          // Optionally show a subtle notification to admin
          if (response.data.old_seat_count !== response.data.new_seat_count) {
            const notification = document.createElement('div');
            notification.className = 'fixed top-4 right-4 bg-blue-600 text-white px-4 py-2 rounded-lg shadow-lg z-50';
            notification.textContent = `Billing updated: ${response.data.new_seat_count} seats ($${response.data.monthly_cost}/month)`;
            document.body.appendChild(notification);
            
            setTimeout(() => {
              document.body.removeChild(notification);
            }, 5000);
          }
        }
      }
    } catch (error) {
      console.error('Error updating billing:', error);
      // Don't show error to user - billing sync can happen later
    }
  };

  // Load team members when component mounts
  useEffect(() => {
    if (currentTenant && isAdmin()) {
      loadTeamMembers();
      loadBillRateRoles(); // NEW: Load bill rate roles from database
    }
  }, [currentTenant]);

  const loadTeamMembers = async () => {
    try {
      setLoadingMembers(true);
      const { data, error } = await supabase
        .from('tenant_memberships')
        .select(`
          *,
          user_profiles!tenant_memberships_user_id_fkey (
            id,
            email,
            full_name,
            avatar_url
          ),
          invited_by_profile:user_profiles!tenant_memberships_invited_by_fkey (
            email,
            full_name
          )
        `)
        .eq('tenant_id', currentTenant.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      console.log('Team members data:', data);
      setTeamMembers(data || []);
    } catch (error) {
      console.error('Error loading team members:', error);
      alert('Error loading team members: ' + error.message);
    } finally {
      setLoadingMembers(false);
    }
  };

  // Enhanced invitation function with email sending
  const handleInviteUser = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim()) {
      setInviteMessage('Email is required');
      return;
    }

    setInviteLoading(true);
    setInviteMessage('');
    setInviteResult(null);

    try {
      // Call our database function to create invitation
      const { data, error } = await supabase.rpc('create_team_invitation', {
        p_tenant_id: currentTenant.id,
        p_invited_email: inviteEmail.trim(),
        p_role: inviteRole,
        p_bill_rate_role: inviteBillRateRole,
        p_invited_by: user.id
      });

      if (error) throw error;

      if (!data.success) {
        throw new Error(data.error);
      }

      setInviteResult(data);
      const inviteLink = `${window.location.origin}/?invite=${data.invitation_token}`;

      if (data.user_exists) {
        // User exists - they were added directly
        setInviteMessage(`✅ User added successfully! ${inviteEmail} is now a member of ${currentTenant.name}.`);
        
        // Update billing for new active member
        await updateBillingAfterTeamChange();
        
        // Still send notification email if enabled
        if (sendEmail) {
          await sendInvitationEmail(data, inviteLink, true);
        }
      } else {
        // User doesn't exist - need to invite them
        let emailSent = false;
        
        if (sendEmail) {
          emailSent = await sendInvitationEmail(data, inviteLink, false);
        }
        
        if (emailSent) {
          setInviteMessage(`📧 Invitation email sent to ${inviteEmail}! They'll receive instructions to join ${currentTenant.name}.`);
        } else if (sendEmail) {
          // Email failed, show link option
          await copyToClipboard(inviteLink);
          setInviteMessage(`⚠️ Email sending failed, but invitation created! Link copied to clipboard. Please share it manually with ${inviteEmail}.`);
        } else {
          // User chose not to send email
          await copyToClipboard(inviteLink);
          setInviteMessage(`🔗 Invitation created! Link copied to clipboard. Share this with ${inviteEmail}: ${inviteLink}`);
        }
      }

      // Refresh team members list
      await loadTeamMembers();
      
      // Reset form
      setInviteEmail('');
      setInviteRole('user');
      setInviteBillRateRole(billRateRoles.length > 0 ? billRateRoles[0].value : '');
      setSendEmail(true);

    } catch (error) {
      console.error('Error inviting user:', error);
      setInviteMessage('❌ Error: ' + error.message);
    } finally {
      setInviteLoading(false);
    }
  };

  // Send invitation email via Resend
  const sendInvitationEmail = async (inviteData, inviteLink, isExistingUser) => {
    try {
      const emailData = {
        tenant_name: inviteData.tenant_name || currentTenant.name,
        inviter_name: inviteData.inviter_name || user.email,
        role: inviteRole,
        bill_rate_role: inviteBillRateRole,
        invitation_url: inviteLink,
        is_existing_user: isExistingUser
      };

      // Use Supabase Edge Function to send email
      const { data: emailResult, error: emailError } = await supabase.functions.invoke('send-custom-email', {
        body: { 
          type: 'team_invitation', 
          email: inviteData.invited_email || inviteEmail, 
          data: emailData 
        }
      });

      if (emailError) {
        console.warn('Email sending failed:', emailError);
        return false;
      }

      console.log('Email sent successfully:', emailResult);
      return true;
    } catch (error) {
      console.error('Error sending invitation email:', error);
      return false;
    }
  };

  // Copy to clipboard helper
  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (err) {
      console.warn('Clipboard API failed, using fallback');
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        return true;
      } catch (fallbackErr) {
        console.error('Fallback copy failed:', fallbackErr);
        return false;
      } finally {
        document.body.removeChild(textArea);
      }
    }
  };

  const handleUpdateRole = async (membershipId, newRole) => {
    try {
      const { error } = await supabase
        .from('tenant_memberships')
        .update({ role: newRole })
        .eq('id', membershipId);

      if (error) throw error;

      await loadTeamMembers();
      alert('Role updated successfully!');
    } catch (error) {
      console.error('Error updating role:', error);
      alert('Error updating role: ' + error.message);
    }
  };

  const handleRemoveMember = async (membershipId, memberEmail, invitedEmail) => {
    const emailToShow = memberEmail || invitedEmail;
    
    if (!window.confirm(`Are you sure you want to remove ${emailToShow} from ${currentTenant.name}?`)) {
      return;
    }

    try {
      const { error } = await supabase
        .from('tenant_memberships')
        .delete()
        .eq('id', membershipId);

      if (error) throw error;

      // Update billing after removing member
      await updateBillingAfterTeamChange();

      // Refresh team members list
      await loadTeamMembers();
      
      alert(`${emailToShow} has been removed from ${currentTenant.name}.`);
    } catch (error) {
      console.error('Error removing member:', error);
      alert('Error removing member: ' + error.message);
    }
  };

  const copyInviteLink = async (token) => {
    const inviteLink = `${window.location.origin}/?invite=${token}`;
    const copied = await copyToClipboard(inviteLink);
    if (copied) {
      alert('Invitation link copied to clipboard!');
    } else {
      prompt('Copy this invitation link:', inviteLink);
    }
  };

  // Resend invitation email
  const resendInvitationEmail = async (member) => {
    if (!member.invitation_token) return;

    try {
      const inviteLink = `${window.location.origin}/?invite=${member.invitation_token}`;
      const emailData = {
        tenant_name: currentTenant.name,
        inviter_name: user.email,
        role: member.role,
        bill_rate_role: member.bill_rate_role,
        invitation_url: inviteLink,
        is_existing_user: false
      };

      const { data: emailResult, error: emailError } = await supabase.functions.invoke('send-custom-email', {
        body: { 
          type: 'team_invitation', 
          email: member.invited_email, 
          data: emailData 
        }
      });

      if (emailError) throw emailError;

      alert(`Invitation email resent to ${member.invited_email}!`);
    } catch (error) {
      console.error('Error resending email:', error);
      alert('Failed to resend email. You can copy the invitation link instead.');
    }
  };

  const getStatusBadge = (member) => {
    if (member.status === 'active') {
      return <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-700">Active</span>;
    } else if (member.status === 'invited') {
      const isExpired = new Date(member.invitation_expires_at) < new Date();
      if (isExpired) {
        return <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-700">Expired</span>;
      } else {
        return <span className="px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-700">Pending</span>;
      }
    } else {
      return <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-700">Inactive</span>;
    }
  };

  const getRoleBadge = (role) => {
    const roleStyles = {
      admin: 'bg-purple-100 text-purple-700',
      manager: 'bg-blue-100 text-blue-700',
      user: 'bg-slate-100 text-slate-700',
    };
    
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${roleStyles[role] || roleStyles.user}`}>
        {role.charAt(0).toUpperCase() + role.slice(1)}
      </span>
    );
  };

  if (!isAdmin()) {
    return (
      <div className="bg-white rounded-2xl p-12 shadow-sm border border-slate-200 text-center">
        <Users className="w-16 h-16 mx-auto mb-6 text-slate-300" />
        <h3 className="text-2xl font-semibold text-slate-800 mb-4">Access Restricted</h3>
        <p className="text-slate-600">Only administrators can manage team members.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Billing Info */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-xl font-semibold text-slate-800">Team Members</h3>
            <p className="text-slate-600 mt-1">Manage your organization's team members and permissions</p>
            
            {/* Billing summary */}
            {currentTenant?.subscription_status === 'active' && (
              <div className="mt-3 flex items-center space-x-6 text-sm text-slate-600">
                <span>
                  Active seats: <strong className="text-slate-800">{teamMembers.filter(m => m.status === 'active').length}</strong>
                </span>
                <span>
                  Monthly cost: <strong className="text-slate-800">${(teamMembers.filter(m => m.status === 'active').length * 6.99).toFixed(2)}</strong>
                </span>
              </div>
            )}
          </div>
          <button
            onClick={() => setShowInviteModal(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl flex items-center space-x-2 transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>Invite Member</span>
          </button>
        </div>
      </div>

      {/* Team members table */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
        {loadingMembers ? (
          <div className="p-12 text-center">
            <div className="text-lg text-slate-500">Loading team members...</div>
          </div>
        ) : teamMembers.length === 0 ? (
          <div className="p-12 text-center text-slate-500">
            <Users className="w-12 h-12 mx-auto mb-4 text-slate-300" />
            <div className="text-lg font-medium mb-2">No team members yet</div>
            <div>Invite your first team member to get started</div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Member</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Role</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Bill Rate Role</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Joined</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-200">
                {teamMembers.map((member) => {
                  const isExpired = member.invitation_expires_at && new Date(member.invitation_expires_at) < new Date();
                  
                  return (
                    <tr key={member.id} className="hover:bg-slate-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-slate-200 rounded-full flex items-center justify-center">
                            {member.user_profiles ? (
                              <span className="text-sm font-medium text-slate-700">
                                {member.user_profiles.full_name?.charAt(0) || member.user_profiles.email?.charAt(0) || '?'}
                              </span>
                            ) : (
                              <Mail className="w-5 h-5 text-slate-500" />
                            )}
                          </div>
                          <div>
                            <div className="font-medium text-slate-900">
                              {member.user_profiles?.full_name || member.invited_email}
                            </div>
                            <div className="text-sm text-slate-500">
                              {member.user_profiles?.email || member.invited_email}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {member.user_id === user.id ? (
                          getRoleBadge(member.role)
                        ) : (
                          <select
                            value={member.role}
                            onChange={(e) => handleUpdateRole(member.id, e.target.value)}
                            className="text-sm border border-slate-300 rounded-lg px-3 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="user">User</option>
                            <option value="manager">Manager</option>
                            <option value="admin">Admin</option>
                          </select>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm text-slate-600">
                          {member.bill_rate_role ? formatRoleName(member.bill_rate_role) : 'Not set'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(member)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                        {member.created_at ? new Date(member.created_at).toLocaleDateString() : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          {member.status === 'invited' && member.invitation_token && (
                            <>
                              <button
                                onClick={() => copyInviteLink(member.invitation_token)}
                                className="text-blue-600 hover:text-blue-800 transition-colors"
                                title="Copy invite link"
                              >
                                <Copy className="w-4 h-4" />
                              </button>
                              {isExpired && (
                                <button
                                  onClick={() => resendInvitationEmail(member)}
                                  className="text-green-600 hover:text-green-800 transition-colors"
                                  title="Resend email"
                                >
                                  <Mail className="w-4 h-4" />
                                </button>
                              )}
                            </>
                          )}
                          {member.user_id !== user.id && (
                            <button
                              onClick={() => handleRemoveMember(member.id, member.user_profiles?.email, member.invited_email)}
                              className="text-red-600 hover:text-red-800 transition-colors"
                              title="Remove member"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Invite Member Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-slate-800">Invite Team Member</h2>
                <button
                  onClick={() => {
                    setShowInviteModal(false);
                    setInviteMessage('');
                    setInviteResult(null);
                  }}
                  className="text-slate-400 hover:text-slate-600 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <form onSubmit={handleInviteUser} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                    placeholder="colleague@company.com"
                    required
                    disabled={inviteLoading}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Permission Role
                  </label>
                  <select
                    value={inviteRole}
                    onChange={(e) => setInviteRole(e.target.value)}
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                    disabled={inviteLoading}
                  >
                    <option value="user">User - Can track time and view assigned projects</option>
                    <option value="manager">Manager - Can manage projects and approve time entries</option>
                    <option value="admin">Admin - Full access to all features</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Bill Rate Role
                  </label>
                  {loadingBillRates ? (
                    <div className="w-full px-4 py-3 border border-slate-300 rounded-lg bg-slate-50 text-slate-500">
                      Loading bill rate roles...
                    </div>
                  ) : (
                    <select
                      value={inviteBillRateRole}
                      onChange={(e) => setInviteBillRateRole(e.target.value)}
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                      disabled={inviteLoading}
                      required
                    >
                      {billRateRoles.length === 0 ? (
                        <option value="">No bill rate roles available</option>
                      ) : (
                        billRateRoles.map(role => (
                          <option key={role.value} value={role.value}>
                            {role.label} - ${role.rate}/hour
                          </option>
                        ))
                      )}
                    </select>
                  )}
                  {billRateRoles.length === 0 && !loadingBillRates && (
                    <p className="text-sm text-amber-600 mt-1">
                      ⚠️ No bill rate roles found. Please set up bill rates first in the Billing section.
                    </p>
                  )}
                </div>

                {/* Email sending option */}
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <label className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={sendEmail}
                      onChange={(e) => setSendEmail(e.target.checked)}
                      className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
                      disabled={inviteLoading}
                    />
                    <div>
                      <span className="text-sm font-medium text-slate-700">📧 Send invitation email</span>
                      <p className="text-xs text-slate-600">
                        {sendEmail 
                          ? "We'll send an invitation email. If it fails, we'll provide a link to copy."
                          : "We'll create the invitation and copy the link for you to share manually."
                        }
                      </p>
                    </div>
                  </label>
                </div>

                {inviteMessage && (
                  <div className={`text-sm p-3 rounded-lg ${
                    inviteMessage.includes('❌') || inviteMessage.includes('⚠️')
                      ? 'bg-red-50 text-red-700 border border-red-200'
                      : 'bg-green-50 text-green-700 border border-green-200'
                  }`}>
                    {inviteMessage}
                  </div>
                )}
                
                <div className="flex justify-end space-x-4 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowInviteModal(false);
                      setInviteMessage('');
                      setInviteResult(null);
                    }}
                    className="px-6 py-3 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors"
                    disabled={inviteLoading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={inviteLoading || billRateRoles.length === 0}
                    className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center space-x-2"
                  >
                    {inviteLoading && (
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    )}
                    <span>{inviteLoading ? 'Sending...' : 'Send Invite'}</span>
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamManagementView;